/**
 * Copyright (c) S2 Partnership Ltd. 2022
 *
 * A service that implements conditional logic on the incident forms
 *
 * @summary Implements conditional logic on incident forms
 * @author S2
 *
 * Created at     : 17-06-2021
 */

import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import { IncidentFormConfigurationConditionalLogic } from '../view-models/smart-sync';

@Injectable({
  providedIn: 'root'
})
export class ConditionalLogicService {
  subject = new Subject<boolean>();
  form: UntypedFormGroup;
  formSubscription: Subscription;

  componentShowHideFnMap = new Map();
  componentVisibilityFnMap = new Map();
  conditionalLogic: IncidentFormConfigurationConditionalLogic[];
  actionsShowFields: number[] = [];
  actionsHideFields: number[] = [];
  actionsFlags: string[] = [];

  /**
   * Standard Angular Constructor.
   *
   * @ignore
   */
  constructor() {
    // Empty constructor.
  }

  /**
   * Setter for the conditional logic for this incident form.
   *
   * @arg {IncidentFormConfigurationConditionalLogic[]} conditionalLogic The array of conditional logic for this incident form.
   */
  setFormConditionalLogic(conditionalLogic: IncidentFormConfigurationConditionalLogic[]) {
    this.conditionalLogic = conditionalLogic;
  }

  /**
   * Setter for the incident form used to construct the current incident view.
   *
   * @arg {any} form The form used to construct the current incident view.
   */
  setForm(form: any) {
    this.form = form;
  }

  /**
   * Gets the Action flags used by conditional logic.
   *
   * @return {string[]} A string array of action flags.
   */
  getActionsFlags() {
    return [...new Set(this.actionsFlags)]; // unique
  }

  /**
   * Runs the conditional logic.
   *
   * @arg {boolean} initCheck Boolean conditional to initalise the check.
   */
  runConditionalLogicCheck(initCheck = false) {
    this.actionsShowFields = [];
    this.actionsHideFields = [];
    this.actionsFlags = [];

    this.form.value.tabs.forEach(tab => {
      tab.groups.forEach(group => {
        group.questions.forEach(question => {
          if (initCheck || question.value) {
            this.conditionalLogic.forEach((logic: any) => {
              let conditions = logic.rule.rules.conditions.filter(cond => cond.if === question.id);

              if (!conditions.length) {
                for (const action of logic.rule.rules.actions) {
                  const fieldId = action.field;
                  if (!Array.isArray(fieldId)) {
                    if (fieldId === question.id) {
                      conditions = logic.rule.rules.conditions;
                    }
                  } else if (fieldId.find(item2 => item2 === question.id)) {
                    conditions = logic.rule.rules.conditions;
                  }
                }
              }

              const actions = logic.rule.rules.actions;
              if (logic.rule.rules.switch === 'any') {
                this.verifyConditionsAsAnyAndExecuteActions(question, conditions, actions, logic.rule.rules.flag);
              } else if (logic.rule.rules.switch === 'all') {
                this.verifyConditionsAsAllAndExecuteActions(question, conditions, actions, logic.rule.rules.flag);
              }

            });
          }
        });
      });
    });

    this.executeActions();
  }

  /**
   * Verifies conditions as any aka "OR" and executes actions.
   *
   * @arg {any} question Json object.
   * @arg {any} conditions Json object.
   * @arg {any} actions Json object.
   * @arg {any} flag Boolean to push flags to array.
   */
  verifyConditionsAsAnyAndExecuteActions(question: any, conditions: any, actions: any, flag: any) {
    let conditionFields = [];

    // Filtering conditions by field ID.
    conditions.forEach(condition => {
      conditionFields.push(condition.if);
    });

    // Unique field IDs.
    conditionFields = [...new Set(conditionFields)];
    // Checking conditions for each field id and executing proper action.
    conditionFields.forEach(fieldId => {
      let executeMatchAction = false;
      conditions.filter(condition => condition.if === fieldId).forEach(condition => {
        if (condition.if === question.id && this.componentShowHideFnMap.has(question.id)) {

          if (this.checkConditionValueByState(condition, question)) {
            executeMatchAction = true;
          }
        }
      });

      if (executeMatchAction) {
        if (flag) {
          this.actionsFlags.push(flag);
        }

        this.assignFieldsToShowList(actions);
      } else {
        this.assignFieldsToHideList(actions);
      }
    });
  }

  /**
   * Verifies conditions as all aka "AND" and executes actions.
   *
   * @arg {any} question Json object.
   * @arg {any} conditions Json object.
   * @arg {any} actions Json object.
   * @arg {any} flag Boolean to push flags to array.
   */
  verifyConditionsAsAllAndExecuteActions(question: any, conditions: any, actions: any, flag: any) {
    let conditionFields = [];

    // Filtering conditions by field ID.
    conditions.forEach(condition => {
      conditionFields.push(condition.if);
    });

    // unique field IDs
    conditionFields = [...new Set(conditionFields)];
    let executeMatchAllActions = [];
    // Checking conditions for each field id and marking it to execute or not.
    conditionFields.forEach(fieldId => {
      let executeMatchAction = false;
      conditions.filter(condition => condition.if === fieldId).forEach(condition => {
        if (condition.if === question.id && this.componentShowHideFnMap.has(question.id)) {
          if (this.checkConditionValueByState(condition, question)) {
            executeMatchAction = true;
          }
        }
      });

      executeMatchAllActions.push(executeMatchAction);
    });

    executeMatchAllActions = [...new Set(executeMatchAllActions)];


    if (executeMatchAllActions.length === 1 && executeMatchAllActions[0] === true) {
      conditionFields.forEach(() => {
        if (flag) {
          this.actionsFlags.push(flag);
        }
        this.assignFieldsToShowList(actions);
      });
    } else {
      conditionFields.forEach(() => {
        this.assignFieldsToHideList(actions);
      });
    }
  }

  /**
   * Checks condition value by state.
   *
   * @arg {any} condition Json object.
   * @arg {any} question Json object.
   */
  checkConditionValueByState(condition: any, question: any) {
    if ((this.componentVisibilityFnMap.has(condition.if) && this.componentVisibilityFnMap.get(condition.if)() === false) || !question.value) {
      return false;
    }

    if (condition.state === '==') {
      if (!Array.isArray(question.value) && (condition.value === question.value.id || condition.value === question.value)) {
        return true;
      } else if (Array.isArray(question.value)) {
        question.value.forEach(value => {
          if (condition.value === value.id || condition.value === value) {
            return true;
          }
        });
      }
    } else if (condition.state === '!=') {
      if (!Array.isArray(question.value) && (condition.value !== question.value.id || condition.value !== question.value)) {
        return true;
      } else if (Array.isArray(question.value)) {
        question.value.forEach(value => {
          if (condition.value !== value.id || condition.value !== value) {
            return true;
          }
        });
      }
    }

    return false;
  }

  /**
   * Assigns fields to show as list.
   *
   * @arg {any} actions Json object.
   */
  assignFieldsToShowList(actions: any) {
    actions.forEach(action => {
      if (action.do === 'show') {
        this.actionsShowFields.push(action.field);
      } else if (action.do === 'show_multiple') {
        action.field.forEach(fId => {
          this.actionsShowFields.push(fId);
        });
      }
    });
  }

  /**
   * Assigns fields to hide as list.
   *
   * @arg {any} actions Json object.
   */
  assignFieldsToHideList(actions: any) {
    actions.forEach(action => {
      if (action.do === 'show') {
        this.actionsHideFields.push(action.field);
      } else if (action.do === 'show_multiple') {
        action.field.forEach(fId => {
          this.actionsHideFields.push(fId);
        });
      }
    });
  }

  /**
   * Executes actions.
   */
  executeActions() {
    // Unique field IDs.
    this.actionsShowFields = [...new Set(this.actionsShowFields)];
    // Unique field IDs.
    this.actionsHideFields = [...new Set(this.actionsHideFields)];

    for (const fieldId of this.actionsShowFields) {
      this.showFieldWithId(fieldId);
    }

    for (const fieldId of this.actionsHideFields) {
      if (this.actionsShowFields.indexOf(fieldId) === -1) {
        this.hideFieldWithId(fieldId);
      }
    }
  }

  /**
   * Shows Field by ID.
   *
   * @arg {number} fieldId ID of field to show.
   */
  showFieldWithId(fieldId: number) {
    if (this.componentShowHideFnMap.has(fieldId)) {
      this.componentShowHideFnMap.get(fieldId)(true);
    }
  }

  /**
   * Hide Field by ID.
   *
   * @arg {number} fieldId ID of field to show.
   */
  hideFieldWithId(fieldId: number) {
    if (this.componentShowHideFnMap.has(fieldId)) {
      this.componentShowHideFnMap.get(fieldId)(false);
    }
  }

  /**
   * Add component to map.
   *
   * @arg {number} id ID of field.
   * @arg {any} showHideFn Bind.
   * @arg {any} visibilityFn Bind.
   */
  addComponentToMap(id: number, showHideFn: any, visibilityFn: any) {
    this.componentShowHideFnMap.set(id, showHideFn);
    this.componentVisibilityFnMap.set(id, visibilityFn);
  }

  /**
   * Remove component from map.
   *
   * @arg {number} id ID of field.
   */
  removeComponentFromMap(id: number) {
    this.componentShowHideFnMap.delete(id);
    this.componentVisibilityFnMap.delete(id);
  }

  /**
   * Clears component from map.
   */
  clearComponentsMap() {
    this.componentShowHideFnMap.clear();
    this.componentVisibilityFnMap.clear();
  }

  /**
   * Checks if field is visible.
   *
   * @arg {number} id ID of field.
   * @return {any} Mixed boolean or number.
   */
  checkIfFieldIsVisible(id: number): any {
    if (this.componentVisibilityFnMap.has(id)) {
      return this.componentVisibilityFnMap.get(id)();
    }
    return false;
  }

}
